/**
 * Preflight readiness for the top-level "Generate Schedule" button.
 *
 * The generate MODAL only ever offers Ready divisions
 * (`divisions.filter((d) => d.isReady)`), and `isReady` is a
 * user-controlled backend flag (`is_scheduler_ready`) with no
 * team-count guard — so a Ready division can still have <2 teams,
 * and a division with ≥2 teams can be left in Draft.
 *
 * This helper therefore scopes the gate to Ready divisions so the
 * button and the modal agree:
 *   - Draft divisions with 0–1 teams no longer block generation.
 *   - All-Draft (even with ≥2 teams each) blocks the button instead
 *     of opening an empty modal.
 *
 * Pure function — no React. Validated by generateValidation.test.ts.
 */

/** Single source of truth for the per-division minimum team count. */
export const MIN_TEAMS_PER_DIVISION = 2

export interface GenerateReadinessDivision {
  isReady: boolean
  teamsCount: number
}

export interface GenerateReadinessInput {
  hasDateRange: boolean
  venueCount: number
  activeTimeSlotCount: number
  divisions: ReadonlyArray<GenerateReadinessDivision>
  hasUnconfiguredVenueCapacityConstraint: boolean
}

export interface GenerateReadinessResult {
  canGenerate: boolean
  generateDisabledReason: string
  generateIssueCount: number
}

export const evaluateGenerateReadiness = (
  input: GenerateReadinessInput
): GenerateReadinessResult => {
  const {
    hasDateRange,
    venueCount,
    activeTimeSlotCount,
    divisions,
    hasUnconfiguredVenueCapacityConstraint,
  } = input

  // Preserve the exact existing issue-push order so unchanged
  // messages stay byte-identical:
  // date range → venue → time slot → divisions → venue-capacity.
  const issues: string[] = []
  if (!hasDateRange) {
    issues.push('a date range')
  }
  if (venueCount === 0) {
    issues.push('at least one venue')
  }
  if (activeTimeSlotCount === 0) {
    issues.push('at least one time slot')
  }

  // Divisions block — scoped to Ready divisions, matching the modal.
  const readyDivisions = divisions.filter((d) => d.isReady)
  if (divisions.length === 0) {
    issues.push('at least one division')
  } else if (readyDivisions.length === 0) {
    issues.push('at least one ready division')
  } else if (
    !readyDivisions.every((d) => d.teamsCount >= MIN_TEAMS_PER_DIVISION)
  ) {
    issues.push('at least 2 teams per ready division')
  }

  if (hasUnconfiguredVenueCapacityConstraint) {
    issues.push('facilities for Venue Capacity constraint')
  }

  if (issues.length > 0) {
    return {
      canGenerate: false,
      generateDisabledReason: `Configure ${issues.join(
        ', '
      )} before generating`,
      generateIssueCount: issues.length,
    }
  }

  return {
    canGenerate: true,
    generateDisabledReason: '',
    generateIssueCount: 0,
  }
}
